import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { useState } from "react";
import Watermark from "../Watermark";

const FEATURE_GROUPS = [
  {
    title: "Authentication & Users",
    desc: "User access, identity, and permission control",
    features: [
      { key: "login", label: "User login & signup" },
      { key: "socialAuth", label: "Social login (Google, Apple, etc.)" },
      { key: "roles", label: "User roles & permissions" },
    ],
  },
  {
    title: "Payments & Monetization",
    desc: "Revenue, billing, and subscriptions",
    features: [
      { key: "payments", label: "Online payments (Stripe, Razorpay, etc.)" },
      { key: "subscriptions", label: "Subscriptions & plans" },
      { key: "invoices", label: "Invoices & billing management" },
    ],
  },
  {
    title: "Core App Features",
    desc: "Essential functionality of your product",
    features: [
      { key: "dashboard", label: "Admin dashboard" },
      { key: "notifications", label: "Email / push notifications" },
      { key: "search", label: "Search & filters" },
      { key: "uploads", label: "File uploads & media" },
    ],
  },
  {
    title: "Integrations & Advanced",
    desc: "External systems and advanced capabilities",
    features: [
      { key: "api", label: "Third-party API integration" },
      { key: "analytics", label: "Analytics & tracking" },
      { key: "ai", label: "AI / automation features" },
    ],
  },
];

const Step3Features = ({ data, onBack, onNext }) => {
  const [selected, setSelected] = useState(data?.features || []);

  const toggleFeature = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key],
    );
  };

  return (
    <Box position="relative" mt={{ xs: 4, md: 6 }}>
      {/* ===== HEADER (SAME AS STEP 2) ===== */}
      <Box
        textAlign="center"
        mb={{ xs: 4, md: 5 }}
        sx={{ maxWidth: 760, mx: "auto" }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--color-midnight-60)",
            mb: 0.8,
          }}
        >
          Features & Integrations
        </Typography>

        <Typography
          component="h3"
          sx={{
            fontSize: { xs: "1.45rem", sm: "1.6rem", md: "1.85rem" },
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            color: "var(--color-midnight)",
          }}
        >
          What functionality{" "}
          <Box component="span" sx={{ color: "var(--color-fiery)" }}>
            do you need
          </Box>
          ?
        </Typography>

        <Typography
          sx={{
            mt: 1.2,
            fontSize: { xs: "0.9rem", md: "1.05rem" },
            lineHeight: 1.65,
            color: "var(--color-midnight-70)",
          }}
        >
          Select all features required for your product. Advanced capabilities
          increase development effort and cost.
        </Typography>
      </Box>

      {/* ===== FEATURE GROUPS ===== */}
      <Stack spacing={{ xs: 3, md: 4 }}>
        {FEATURE_GROUPS.map((group) => (
          <Paper
            key={group.title}
            elevation={0}
            sx={{
              p: { xs: 2.25, sm: 2.75, md: 3.5 },
              borderRadius: { xs: 1.5, md: 2 },
              border: "1px solid var(--color-midnight-15)",
              background:
                "linear-gradient(180deg, #FFFFFF 0%, rgba(246,247,235,0.55) 100%)",
            }}
          >
            {/* Group Header */}
            <Box mb={2}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "var(--color-midnight)",
                }}
              >
                {group.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "var(--color-midnight-70)",
                }}
              >
                {group.desc}
              </Typography>
            </Box>

            {/* Feature Cards */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "flex-start",
              }}
            >
              {group.features.map((feature) => {
                const active = selected.includes(feature.key);

                return (
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleFeature(feature.key)}
                    sx={{
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: {
                        xs: "100%",
                        sm: "calc(50% - 12px)",
                        md: "calc(33.333% - 12px)",
                      },
                      maxWidth: {
                        xs: "100%",
                        sm: "calc(50% - 12px)",
                        md: "calc(33.333% - 12px)",
                      },

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.2,

                      px: 2,
                      py: 1.4,
                      borderRadius: "999px",

                      border: active
                        ? "1.5px solid var(--color-fiery)"
                        : "1px solid var(--color-midnight-15)",

                      backgroundColor: active ? "rgba(233,79,55,0.10)" : "#fff",

                      transition: "all 0.2s ease",

                      "&:hover": {
                        backgroundColor: active
                          ? "rgba(233,79,55,0.14)"
                          : "rgba(0,0,0,0.03)",
                      },
                      cursor:"pointer"
                    }}
                  >
                    {/* Label */}
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: "var(--color-midnight)",

                        /* 🔑 Prevent width explosion */
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {feature.label}
                    </Typography>

                    {/* Selection Indicator */}
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,

                        backgroundColor: active
                          ? "var(--color-fiery)"
                          : "var(--color-midnight-15)",

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {active && (
                        <CheckRoundedIcon
                          sx={{ fontSize: 12, color: "#fff" }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* ===== ACTION BAR (SAME AS STEP 2) ===== */}
      <Paper
        elevation={0}
        sx={{
          mt: { xs: 4, md: 5 },
          p: { xs: 2, md: 2 },
          borderRadius: 2,
          backgroundColor: "rgba(246,247,235,0.6)",
          border: "1px solid var(--color-midnight-15)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="outlined"
            fullWidth
            sx={{ maxWidth: { sm: 160 } }}
            onClick={onBack}
          >
            Back
          </Button>

          <Button
            variant="contained"
            color="secondary"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              maxWidth: { sm: 260 },
              fontWeight: 600,
              boxShadow: "0 10px 24px rgba(233,79,55,0.35)",
              position: "relative",
              overflow: "hidden",

              /* 🔥 Subtle pulse */
              animation: "ctaPulse 2.6s ease-in-out infinite",

              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
            onClick={() => onNext({ features: selected })}
          >
            View Cost Estimate
          </Button>
        </Stack>
      </Paper>

      {/* ===== Watermark ===== */}
      <Watermark />
    </Box>
  );
};

export default Step3Features;
